import React, { useEffect, useState } from 'react';
import { VStack, Text, Input, Checkbox, Box } from '@chakra-ui/react';
import ModalShell from '../../../../components/ui/ModalShell';
import { ff } from '../../../../theme';

interface EditZipModalProps {
  isOpen: boolean;
  initialZip: string;
  onSave: (zip: string, setAsDefault: boolean) => void;
  onClose: () => void;
}

// Bottom sheet to change the zip that drives "closest to you". The checkbox only controls whether
// the change is remembered next visit — the zip always applies to the current session on save.
const EditZipModal: React.FC<EditZipModalProps> = ({ isOpen, initialZip, onSave, onClose }) => {
  const [zip, setZip] = useState(initialZip);
  const [setAsDefault, setSetAsDefault] = useState(true);

  // Reset the field each time the sheet opens so it reflects the current default.
  useEffect(() => {
    if (isOpen) {
      setZip(initialZip);
      setSetAsDefault(true);
    }
  }, [isOpen, initialZip]);

  const isValid = /^\d{5}$/.test(zip.trim());

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Edit default zip code"
      cancelLabel="Cancel"
      primary={{ label: 'Save', onClick: () => onSave(zip.trim(), setAsDefault), tone: 'lime', isDisabled: !isValid }}
    >
      <VStack spacing="16px" align="stretch">
        <Text fontSize="15px" lineHeight="1.45" color={ff.body}>
          This helps us show locations closest to you.
        </Text>

        <Box as="label" display="flex" flexDirection="column" gap="6px">
          <Text as="span" fontSize="14px" fontWeight={600}>Zip code</Text>
          <Input
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
            placeholder="e.g. 78704"
            inputMode="numeric"
            h="48px"
            px="14px"
            borderRadius="14px"
            border={`1px solid ${ff.border}`}
            bg="white"
            fontSize="16px"
            _hover={{ borderColor: ff.border }}
            _focusVisible={{ borderColor: ff.ink, boxShadow: 'none' }}
            autoFocus
          />
        </Box>

        <Checkbox
          isChecked={setAsDefault}
          onChange={(e) => setSetAsDefault(e.target.checked)}
          alignItems="flex-start"
          p="14px"
          borderRadius="16px"
          border={setAsDefault ? `2px solid ${ff.ink}` : `1px solid ${ff.border}`}
          bg={setAsDefault ? ff.limeSurface : 'white'}
          sx={{
            '.chakra-checkbox__control': { w: '22px', h: '22px', borderRadius: '6px', borderColor: ff.ink, mt: '1px' },
            '.chakra-checkbox__control[data-checked]': { bg: ff.ink, borderColor: ff.ink, color: ff.lime },
          }}
        >
          <Box ml="4px">
            <Text fontSize="15px" fontWeight={600} lineHeight="1.4">Set as my new default zip code</Text>
            <Text fontSize="13px" lineHeight="1.4" color={ff.text2}>
              This will be used to show your closest locations next time you visit.
            </Text>
          </Box>
        </Checkbox>
      </VStack>
    </ModalShell>
  );
};

export default EditZipModal;
